import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, ArrowRight, ArrowLeft, Check, Building2, Users, Upload, CalendarDays,
  PartyPopper, Plus, Archive, RefreshCw, FileSpreadsheet, GraduationCap,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { ImportStudentsDialog } from "@/components/ImportStudentsDialog";
import { CredentialsPrint } from "@/components/CredentialsPrint";
import { api, formatApiErrorDetail } from "@/lib/api";

const STEPS = [
  { icon: Building2, label: "Établissement" },
  { icon: RefreshCw, label: "Nouvelle année" },
  { icon: Users, label: "Classes" },
  { icon: Upload, label: "Élèves" },
  { icon: CalendarDays, label: "Calendrier" },
  { icon: PartyPopper, label: "Fin" },
];

export default function RentreeWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const [school, setSchool] = useState({ name: "", city: "", postal_code: "", address: "" });
  const [rentree, setRentree] = useState({ school_year: "", active_class_count: 0, archived: false });
  const [classes, setClasses] = useState([]);
  const [newClass, setNewClass] = useState({ name: "", level: "" });
  const [importFor, setImportFor] = useState(null);
  const [cal, setCal] = useState({ options: { academies: [], years: [] }, academie: "", year: "" });
  const [creds, setCreds] = useState([]);
  const [printOpen, setPrintOpen] = useState(false);

  useEffect(() => {
    api.get("/school").then(({ data }) => setSchool({ name: data.name || "", city: data.city || "", postal_code: data.postal_code || "", address: data.address || "" })).catch(() => {});
    api.get("/rentree/status").then(({ data }) => setRentree((r) => ({ ...r, ...data }))).catch(() => {});
    api.get("/calendar/options").then(({ data }) => setCal((c) => ({ ...c, options: data, academie: data.academies[0] || "", year: data.years[0] || "" }))).catch(() => {});
  }, []);

  const upd = (setter) => (k) => (e) => setter((s) => ({ ...s, [k]: e.target.value }));

  const submitSchool = async () => {
    setBusy(true);
    try {
      await api.put("/school", school);
      setStep(2);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const archivePrevious = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rentree/archive");
      setRentree((r) => ({ ...r, archived: true, active_class_count: 0 }));
      toast.success(`${data.archived_count} classe(s) archivée(s). Vous repartez sur une nouvelle année.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addClass = async () => {
    if (!newClass.name.trim()) { toast.error("Le nom de la classe est obligatoire."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/classes", newClass);
      setClasses((c) => [...c, { id: data.id, name: data.name, level: data.level }]);
      setNewClass({ name: "", level: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const importCalendar = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/calendar/import", { params: { year: cal.year, academie: cal.academie } });
      await api.put("/calendar", { periods: data.periods.map((p) => ({ index: p.index, start: p.start, end: p.end })) });
      toast.success("Calendrier officiel importé et enregistré.");
      goToFinish();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const goToFinish = useCallback(async () => {
    setStep(6);
    const all = [];
    for (const c of classes) {
      try {
        const { data } = await api.get(`/classes/${c.id}/credentials`);
        all.push(...(data.families || []));
      } catch { /* ignore */ }
    }
    setCreds(all);
  }, [classes]);

  const Progress = (
    <div className="mb-8 flex items-center justify-between" data-testid="rentree-progress">
      {STEPS.map((s, i) => {
        const n = i + 1;
        const done = step > n;
        const active = step === n;
        return (
          <div key={s.label} className="flex flex-1 flex-col items-center gap-1.5">
            <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
              done ? "bg-emerald-500 text-white" : active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-400"
            }`}>
              {done ? <Check size={16} /> : <s.icon size={16} />}
            </div>
            <span className={`hidden text-[11px] sm:block ${active ? "text-slate-700" : "text-slate-400"}`}>{s.label}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-[#FAFAFA]">
      <div className="mx-auto flex max-w-xl flex-col px-4 py-10">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo size={36} />
            <span className="text-base font-semibold tracking-tight text-slate-900">Assistant de rentrée</span>
          </div>
          <button onClick={() => navigate("/enseignant")} className="text-sm text-slate-400 hover:text-slate-600" data-testid="rentree-quit">Quitter</button>
        </div>

        {Progress}

        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
          className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.04)]"
          data-testid={`rentree-step-${step}`}
        >
          {step === 1 && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">Votre établissement</h1>
              <p className="mt-1 mb-5 text-sm text-slate-500">Vérifiez les informations de votre école.</p>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5"><Label>Nom de l'établissement</Label><Input value={school.name} onChange={upd(setSchool)("name")} data-testid="rentree-school-name" /></div>
                <div className="flex flex-col gap-1.5"><Label>Adresse</Label><Input value={school.address} onChange={upd(setSchool)("address")} data-testid="rentree-school-address" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5"><Label>Code postal</Label><Input value={school.postal_code} onChange={upd(setSchool)("postal_code")} /></div>
                  <div className="flex flex-col gap-1.5"><Label>Ville</Label><Input value={school.city} onChange={upd(setSchool)("city")} /></div>
                </div>
              </div>
              <Button onClick={submitSchool} disabled={busy} className="mt-5 w-full rounded-xl" data-testid="rentree-school-next">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <>Continuer <ArrowRight size={16} className="ml-2" /></>}
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">Nouvelle année scolaire</h1>
              <p className="mt-1 mb-5 text-sm text-slate-500">Année en cours : <span className="font-medium text-slate-700">{rentree.school_year}</span></p>
              {rentree.active_class_count > 0 && !rentree.archived ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="rentree-archive-block">
                  <div className="flex items-start gap-3">
                    <Archive size={18} className="mt-0.5 shrink-0 text-amber-600" />
                    <div className="text-sm text-amber-800">
                      Vous avez <span className="font-semibold">{rentree.active_class_count} classe(s)</span> active(s).
                      Pour repartir sur une nouvelle année, archivez-les. Les données (élèves, passeports, signatures) restent conservées et consultables.
                    </div>
                  </div>
                  <Button onClick={archivePrevious} disabled={busy} variant="outline" className="mt-4 w-full rounded-xl border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="rentree-archive-btn">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <><Archive size={16} className="mr-2" /> Archiver mes classes actuelles</>}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="rentree-fresh">
                  <Check size={18} /> {rentree.archived ? "Classes archivées. Vous partez sur une nouvelle année." : "Aucune classe active : vous partez sur une nouvelle année."}
                </div>
              )}
              <div className="mt-5 flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl" data-testid="rentree-back-2"><ArrowLeft size={16} /></Button>
                <Button onClick={() => setStep(3)} className="flex-1 rounded-xl" data-testid="rentree-year-next">Continuer <ArrowRight size={16} className="ml-2" /></Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">Vos nouvelles classes</h1>
              <p className="mt-1 mb-5 text-sm text-slate-500">Ajoutez les classes de cette année.</p>
              <div className="flex items-end gap-2">
                <div className="flex flex-1 flex-col gap-1.5"><Label>Nom</Label><Input value={newClass.name} onChange={upd(setNewClass)("name")} placeholder="ex : CE2 A" data-testid="rentree-class-name" /></div>
                <div className="flex w-24 flex-col gap-1.5"><Label>Niveau</Label><Input value={newClass.level} onChange={upd(setNewClass)("level")} placeholder="CE2" data-testid="rentree-class-level" /></div>
                <Button onClick={addClass} disabled={busy} variant="outline" className="rounded-xl" data-testid="rentree-add-class"><Plus size={16} /></Button>
              </div>
              {classes.length > 0 && (
                <div className="mt-4 flex flex-col gap-2" data-testid="rentree-classes-list">
                  {classes.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 rounded-xl border border-slate-100 px-3 py-2 text-sm">
                      <GraduationCap size={15} className="text-slate-400" />
                      <span className="font-medium text-slate-700">{c.name}{c.level ? ` · ${c.level}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-5 flex gap-2">
                <Button variant="outline" onClick={() => setStep(2)} className="rounded-xl" data-testid="rentree-back-3"><ArrowLeft size={16} /></Button>
                <Button onClick={() => setStep(4)} disabled={classes.length === 0} className="flex-1 rounded-xl" data-testid="rentree-classes-next">Continuer <ArrowRight size={16} className="ml-2" /></Button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">Importez vos élèves</h1>
              <p className="mt-1 mb-5 text-sm text-slate-500">Importez une liste (.xlsx / .csv) par classe. Les comptes parents sont créés automatiquement.</p>
              <div className="flex flex-col gap-2">
                {classes.map((c) => (
                  <button key={c.id} onClick={() => setImportFor(c.id)}
                    className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 text-left text-sm transition-transform hover:scale-[0.99]"
                    data-testid={`rentree-import-${c.id}`}>
                    <span className="font-medium text-slate-700">{c.name}</span>
                    <span className="flex items-center gap-1.5 text-xs text-slate-500"><Upload size={14} /> Importer</span>
                  </button>
                ))}
              </div>
              <div className="mt-5 flex gap-2">
                <Button variant="outline" onClick={() => setStep(3)} className="rounded-xl" data-testid="rentree-back-4"><ArrowLeft size={16} /></Button>
                <Button onClick={() => setStep(5)} className="flex-1 rounded-xl" data-testid="rentree-students-next">Continuer <ArrowRight size={16} className="ml-2" /></Button>
              </div>
              <ImportStudentsDialog
                open={!!importFor} onOpenChange={(v) => !v && setImportFor(null)}
                classId={importFor} onImported={() => {}} onRequestPrint={() => setImportFor(null)}
              />
            </>
          )}

          {step === 5 && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">Calendrier scolaire</h1>
              <p className="mt-1 mb-5 text-sm text-slate-500">Importez les périodes officielles de votre académie.</p>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>Académie</Label>
                  <select value={cal.academie} onChange={(e) => setCal((c) => ({ ...c, academie: e.target.value }))} className="h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="rentree-academie">
                    {cal.options.academies.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Année scolaire</Label>
                  <select value={cal.year} onChange={(e) => setCal((c) => ({ ...c, year: e.target.value }))} className="h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="rentree-year-select">
                    {cal.options.years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <Button onClick={importCalendar} disabled={busy} className="mt-5 w-full rounded-xl" data-testid="rentree-calendar-import">
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Importer et terminer"}
              </Button>
              <button onClick={goToFinish} className="mt-3 w-full text-center text-sm text-slate-400 hover:text-slate-600" data-testid="rentree-calendar-skip">Configurer plus tard</button>
            </>
          )}

          {step === 6 && (
            <div className="flex flex-col items-center gap-4 py-6 text-center" data-testid="rentree-finish">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
                <PartyPopper size={30} />
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Rentrée prête !</h1>
              <p className="text-sm text-slate-500">
                {classes.length} classe(s) créée(s). {creds.length > 0 ? `${creds.length} identifiant(s) de familles à distribuer.` : ""}
              </p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                {creds.length > 0 && (
                  <Button onClick={() => setPrintOpen(true)} className="rounded-xl" data-testid="rentree-print-credentials">
                    <FileSpreadsheet size={16} className="mr-2" /> Imprimer les identifiants
                  </Button>
                )}
                <Button variant={creds.length > 0 ? "outline" : "default"} onClick={() => navigate("/enseignant")} className="rounded-xl" data-testid="rentree-go-dashboard">
                  Accéder à mon tableau de bord <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <CredentialsPrint open={printOpen} onClose={() => setPrintOpen(false)} families={creds} title="Identifiants des familles — rentrée" />
    </div>
  );
}
